/**
 * OnboardingNudge — soft, dismissible banner that surfaces the user's current
 * completeness_pct and routes them to /onboarding without blocking the rest of
 * the app.
 *
 * Renders nothing when:
 *  - user not authenticated
 *  - business context query still loading
 *  - completeness >= 100 (form already done)
 *  - already on /onboarding (don't nag while filling)
 *
 * Story Onboarding-3 — replaces the old hard-redirect OnboardingGate.
 */

import { Link, useLocation } from "react-router-dom";
import { Sparkle, X } from "@phosphor-icons/react";
import { useState } from "react";
import { useBusinessContext } from "@/hooks/useBusinessContext";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { REQUIRED_COMPLETENESS_PCT } from "@/features/onboarding/config/steps";
import { cn } from "@/lib/utils";

const DISMISS_KEY = "onboarding-nudge-dismissed-at";
const SNOOZE_MS = 1000 * 60 * 60 * 6; // 6h

function isSnoozed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < SNOOZE_MS;
  } catch {
    return false;
  }
}

function snooze(): void {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    // localStorage unavailable — skip
  }
}

export function OnboardingNudge() {
  const { user } = useCurrentUser();
  const location = useLocation();
  const { query, completenessPct, isComplete } = useBusinessContext();
  const [hidden, setHidden] = useState<boolean>(() => isSnoozed());

  if (!user) return null;
  if (query.isLoading || !query.isFetched) return null;
  if (isComplete) return null;
  // TIER-5c onboarding redux: stop nagging once required steps (Identidade +
  // Profissional) are filled. Optional steps no longer trigger the nudge.
  if (completenessPct >= REQUIRED_COMPLETENESS_PCT) return null;
  if (location.pathname.startsWith("/onboarding")) return null;
  if (hidden) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "relative z-30 flex items-center gap-3 border-b border-primary/30 bg-primary/[0.06] px-4 py-2.5 text-sm",
        "supports-[backdrop-filter]:bg-primary/[0.04] supports-[backdrop-filter]:backdrop-blur",
      )}
    >
      <Sparkle weight="fill" className="size-4 shrink-0 text-primary" aria-hidden />
      <p className="flex-1 text-foreground/90">
        <span className="font-medium">Perfil {completenessPct}% completo.</span>{" "}
        <span className="text-muted-foreground">
          Conclua para personalizarmos sua experiência.
        </span>
      </p>
      <Link
        to="/onboarding"
        className="rounded-md bg-primary/15 px-3 py-1 text-xs font-medium text-primary hover:bg-primary/25"
      >
        Continuar
      </Link>
      <button
        type="button"
        aria-label="Adiar lembrete"
        onClick={() => {
          snooze();
          setHidden(true);
        }}
        className="rounded-md p-1 text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
      >
        <X className="size-4" aria-hidden />
      </button>
    </div>
  );
}
